import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

export interface NavItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  /** Optional trailing content (e.g. an unread count badge). */
  trailing?: React.ReactNode;
}

/**
 * A single row in a vertical tree/nav list — icon + label, big touch
 * target, an obvious active state. Built for things like a folder list
 * (Inbox, Archive, Sent, ...), not a horizontal pill/tab bar: the app
 * shell decides layout (vertical rail, drawer, ...), this just renders one
 * row consistently everywhere it's used.
 */
export const NavItem = React.forwardRef<HTMLButtonElement, NavItemProps>(
  ({ icon: Icon, label, active, trailing, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex h-11 w-full items-center gap-3 rounded-md px-3 text-left text-[15px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust focus-visible:ring-offset-2 focus-visible:ring-offset-cream",
        active
          ? "bg-parchment text-rust-deep"
          // Full-strength hover (not a diluted /60), matching the hover
          // treatment thread-list-item.tsx settled on in the design-
          // revision pass — this rail should feel like the same
          // interactive surface as the rest of the app for desktop
          // pointer users, not a muted afterthought.
          : "text-charcoal hover:bg-parchment-dim",
        className,
      )}
      {...props}
    >
      <Icon
        size={19}
        strokeWidth={1.75}
        className={cn("shrink-0", active ? "text-rust-deep" : "text-muted")}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing}
    </button>
  ),
);
NavItem.displayName = "NavItem";
